/**
 * Hero Building Specification & Generator for Mesh City Preview.
 *
 * Constructs a large 4-story walkable headquarters using Kenney Building Kit modular pieces:
 * - Floor 1 (Y=0.0): Double front doorway, windowed walls, ground floor lobby, first flight of interior stairs leading to Floor 2.
 * - Floor 2 (Y=3.6): Floor slabs with stairwell cutout, perimeter window walls, interior landing, second flight of interior stairs leading to Floor 3.
 * - Floor 3 (Y=7.2): Floor slabs with stairwell cutout, upper office walls with large panoramic windows, landing, third flight of interior stairs leading to Floor 4.
 * - Floor 4 (Y=10.8): Rooftop floor deck with stair exit hatch, protective perimeter borders/guardrails, open observation terrace.
 */

import { BoxCollider, StairRampCollider } from './physics/index.js';

export const HERO_BUILDING_CONFIG = {
  STORY_HEIGHT: 3.6,
  FLOORS_COUNT: 4,
  BUILDING_WIDTH: 12.0,
  BUILDING_DEPTH: 12.0,
  BOUNDS: {
    min: [-6.0, 0.0, -6.0],
    max: [6.0, 14.4, 6.0]
  },
  ENTRANCE_POS: [0.0, 0.0, 6.0],
  ROOFTOP_POS: [0.0, 10.8, 0.0],
  ELEVATIONS: {
    FLOOR_1: 0.0,
    FLOOR_2: 3.6,
    FLOOR_3: 7.2,
    FLOOR_4: 10.8,
    ROOF_HATCH_TOP: 14.4
  }
};

/**
 * Metadata definitions for each of the 4 stories.
 */
export const floorDefinitions = [
  {
    floor: 1,
    elevation: 0.0,
    height: 3.6,
    name: 'Ground Floor Lobby & Reception',
    description: 'Double front doorway entrance, polished stone lobby, reception lounge, and first flight of interior stairs.',
    bounds: { min: [-6.0, 0.0, -6.0], max: [6.0, 3.6, 6.0] }
  },
  {
    floor: 2,
    elevation: 3.6,
    height: 3.6,
    name: 'Mezzanine & Open Workspace',
    description: 'Perimeter window walls, open stairwell void with guardrails, interior landing, and stairs to Floor 3.',
    bounds: { min: [-6.0, 3.6, -6.0], max: [6.0, 7.2, 6.0] }
  },
  {
    floor: 3,
    elevation: 7.2,
    height: 3.6,
    name: 'Executive Suites & Boardroom',
    description: 'Upper office floor with panoramic glass windows, executive lounge, and third flight of stairs to the Rooftop.',
    bounds: { min: [-6.0, 7.2, -6.0], max: [6.0, 10.8, 6.0] }
  },
  {
    floor: 4,
    elevation: 10.8,
    height: 3.6,
    name: 'Observation Rooftop Terrace',
    description: 'Open-air observation terrace with high protective perimeter guardrails, 360-degree city skyline view, and stairhouse.',
    bounds: { min: [-6.0, 10.8, -6.0], max: [6.0, 14.4, 6.0] }
  }
];

/**
 * Check if a world point is inside the Hero Building horizontal footprint.
 */
export function isInsideHeroBuilding(x, z) {
  return x >= -6.2 && x <= 6.2 && z >= -6.2 && z <= 6.2;
}

/**
 * Return the floor index (1, 2, 3, 4) for a given Y elevation.
 */
export function getHeroFloorAt(y) {
  if (y < 3.6) return 1;
  if (y < 7.2) return 2;
  if (y < 10.8) return 3;
  return 4;
}

/**
 * Build 3D modular instances for the 4-Story Hero Building
 */
export function getHeroBuildingInstances() {
  const list = [];
  let idCounter = 1;

  function add(name, model, x, y, z, rx = 0, ry = 0, rz = 0, scale = [1.5, 1.5, 1.5], floor = 1, type = 'wall') {
    list.push({
      id: `hero_${idCounter++}_${name}`,
      name,
      type,
      floor,
      modelUrl: `/assets/hero-building/${model}`,
      model: model,
      position: [x, y, z],
      rotation: [rx, ry, rz],
      scale: [...scale],
      castShadow: true,
      receiveShadow: true
    });
  }

  const HALF_PI = Math.PI * 0.5;
  const PI = Math.PI;

  // Scale: modular pieces are 2m wide x 2.4m tall in base. At 1.5x scale, they are 3.0m wide x 3.6m tall!
  const wallScale = [1.5, 1.5, 1.5];
  const floorScale = [1.5, 1.5, 1.5];
  const stairScale = [1.5, 1.5, 1.5];

  // Helper to build 4 perimeter walls for a story
  function buildStoryWalls(floorNum, yBase, hasFrontDoor = false) {
    // Back Wall (-Z at z=-6.0)
    for (let x = -4.5; x <= 4.5; x += 3.0) {
      add(`f${floorNum}_wall_back_${x}`, 'wall-window-square-detailed.glb', x, yBase, -6.0, 0, PI, 0, wallScale, floorNum);
    }
    // Left Wall (-X at x=-6.0)
    for (let z = -4.5; z <= 4.5; z += 3.0) {
      add(`f${floorNum}_wall_left_${z}`, 'wall-window-square-detailed.glb', -6.0, yBase, z, 0, HALF_PI, 0, wallScale, floorNum);
    }
    // Right Wall (+X at x=+6.0)
    for (let z = -4.5; z <= 4.5; z += 3.0) {
      add(`f${floorNum}_wall_right_${z}`, 'wall-window-square-detailed.glb', 6.0, yBase, z, 0, -HALF_PI, 0, wallScale, floorNum);
    }
    // Front Wall (+Z at z=+6.0)
    if (hasFrontDoor) {
      // Leave entrance archway completely open (open walk-through)
      add(`f${floorNum}_wall_front_left`, 'wall-window-wide-round-detailed.glb', -4.5, yBase, 6.0, 0, 0, 0, wallScale, floorNum);
      add(`f${floorNum}_wall_front_right`, 'wall-window-wide-round-detailed.glb', 4.5, yBase, 6.0, 0, 0, 0, wallScale, floorNum);
    } else {
      for (let x = -4.5; x <= 4.5; x += 3.0) {
        add(`f${floorNum}_wall_front_${x}`, 'wall-window-square-detailed.glb', x, yBase, 6.0, 0, 0, 0, wallScale, floorNum);
      }
    }
  }

  // --- FLOOR 1 (Y = 0.0) ---
  buildStoryWalls(1, 0.0, true);
  // Floor 1 ground paving slabs
  for (let x = -4.5; x <= 4.5; x += 3.0) {
    for (let z = -4.5; z <= 4.5; z += 3.0) {
      add(`f1_floor_${x}_${z}`, 'floor.glb', x, 0.0, z, 0, 0, 0, floorScale, 1, 'floor');
    }
  }
  // Flight 1 Interior Stairs (ascending from -2.0, 0.0, -3.0 up to -2.0, 3.6, +3.0)
  add('f1_stairs', 'stairs-sides.glb', -2.25, 0.0, -1.0, 0, 0, 0, stairScale, 1, 'stairs');

  // --- FLOOR 2 (Y = 3.6) ---
  buildStoryWalls(2, 3.6, false);
  // Floor 2 slabs (leaving cutout for flight 1 stairs on west side)
  for (let x = -4.5; x <= 4.5; x += 3.0) {
    for (let z = -4.5; z <= 4.5; z += 3.0) {
      if (x === -1.5 && z === 0) continue; // stair void
      add(`f2_floor_${x}_${z}`, 'floor.glb', x, 3.6, z, 0, 0, 0, floorScale, 2, 'floor');
    }
  }
  // Flight 2 Interior Stairs (switchback: ascending from +2.25, 3.6, +3.0 up to +2.25, 7.2, -3.0)
  add('f2_stairs', 'stairs-sides.glb', 2.25, 3.6, 1.0, 0, PI, 0, stairScale, 2, 'stairs');

  // --- FLOOR 3 (Y = 7.2) ---
  buildStoryWalls(3, 7.2, false);
  for (let x = -4.5; x <= 4.5; x += 3.0) {
    for (let z = -4.5; z <= 4.5; z += 3.0) {
      if (x === 1.5 && z === 0) continue; // stair void
      add(`f3_floor_${x}_${z}`, 'floor.glb', x, 7.2, z, 0, 0, 0, floorScale, 3, 'floor');
    }
  }
  // Flight 3 Interior Stairs (ascending from -2.25, 7.2, -3.0 up to -2.25, 10.8, +3.0)
  add('f3_stairs', 'stairs-sides.glb', -2.25, 7.2, -1.0, 0, 0, 0, stairScale, 3, 'stairs');

  // --- FLOOR 4 (Y = 10.8 - ROOFTOP TERRACE) ---
  // Rooftop floor deck
  for (let x = -4.5; x <= 4.5; x += 3.0) {
    for (let z = -4.5; z <= 4.5; z += 3.0) {
      add(`f4_deck_${x}_${z}`, 'floor.glb', x, 10.8, z, 0, 0, 0, floorScale, 4, 'floor');
    }
  }
  // Perimeter high stone borders & guardrails
  for (let x = -4.5; x <= 4.5; x += 3.0) {
    add(`f4_rail_north_${x}`, 'border-high.glb', x, 10.8, -6.0, 0, PI, 0, wallScale, 4, 'border');
    add(`f4_rail_south_${x}`, 'border-high.glb', x, 10.8, 6.0, 0, 0, 0, wallScale, 4, 'border');
  }
  for (let z = -4.5; z <= 4.5; z += 3.0) {
    add(`f4_rail_west_${z}`, 'border-high.glb', -6.0, 10.8, z, 0, HALF_PI, 0, wallScale, 4, 'border');
    add(`f4_rail_east_${z}`, 'border-high.glb', 6.0, 10.8, z, 0, -HALF_PI, 0, wallScale, 4, 'border');
  }
  // Rooftop Stairhouse Enclosure (open walk-through onto terrace)
  add('f4_hatch_wall_back', 'wall.glb', -2.25, 10.8, -3.0, 0, PI, 0, wallScale, 4, 'wall');
  add('f4_hatch_wall_side', 'wall.glb', -3.75, 10.8, 0.0, 0, HALF_PI, 0, wallScale, 4, 'wall');
  add('f4_hatch_roof', 'roof.glb', -2.25, 14.4, 0.0, 0, 0, 0, wallScale, 4, 'roof');

  // Roof accessories (antenna, chimney)
  add('f4_antenna', 'antenna.glb', 4.0, 10.8, 4.0, 0, 0, 0, [2, 2, 2], 4, 'prop');
  add('f4_chimney', 'chimney.glb', 4.0, 10.8, -4.0, 0, 0, 0, [2, 2, 2], 4, 'prop');

  return list;
}

/**
 * Return all Physics Colliders for the 4-Story Hero Building
 */
export function getHeroBuildingColliders() {
  const colliders = [];

  // 1. Exterior Walls (Solid box perimeters for 4 stories)
  for (let floor = 1; floor <= 3; floor++) {
    const yCenter = (floor - 1) * 3.6 + 1.8;
    // Back (-Z)
    colliders.push(new BoxCollider({
      name: `hero_f${floor}_wall_back`,
      center: { x: 0, y: yCenter, z: -6.0 },
      size: { x: 12.2, y: 3.6, z: 0.4 },
      isWalkable: false
    }));
    // Left (-X)
    colliders.push(new BoxCollider({
      name: `hero_f${floor}_wall_left`,
      center: { x: -6.0, y: yCenter, z: 0 },
      size: { x: 0.4, y: 3.6, z: 12.2 },
      isWalkable: false
    }));
    // Right (+X)
    colliders.push(new BoxCollider({
      name: `hero_f${floor}_wall_right`,
      center: { x: 6.0, y: yCenter, z: 0 },
      size: { x: 0.4, y: 3.6, z: 12.2 },
      isWalkable: false
    }));
    // Front (+Z)
    if (floor === 1) {
      // Flanking entrance walls leaving central doorway open at x=0
      colliders.push(new BoxCollider({
        name: 'hero_f1_wall_front_left',
        center: { x: -4.0, y: yCenter, z: 6.0 },
        size: { x: 4.2, y: 3.6, z: 0.4 },
        isWalkable: false
      }));
      colliders.push(new BoxCollider({
        name: 'hero_f1_wall_front_right',
        center: { x: 4.0, y: yCenter, z: 6.0 },
        size: { x: 4.2, y: 3.6, z: 0.4 },
        isWalkable: false
      }));
    } else {
      colliders.push(new BoxCollider({
        name: `hero_f${floor}_wall_front`,
        center: { x: 0, y: yCenter, z: 6.0 },
        size: { x: 12.2, y: 3.6, z: 0.4 },
        isWalkable: false
      }));
    }
  }

  // 2. Floor Slabs (Walkable horizontal planes)
  colliders.push(new BoxCollider({
    name: 'hero_floor1_slab',
    center: { x: 0, y: 0.05, z: 0 },
    size: { x: 12.0, y: 0.1, z: 12.0 },
    isWalkable: true
  }));
  colliders.push(new BoxCollider({
    name: 'hero_floor2_slab',
    center: { x: 1.5, y: 3.6, z: 0 },
    size: { x: 9.0, y: 0.1, z: 12.0 },
    isWalkable: true
  }));
  colliders.push(new BoxCollider({
    name: 'hero_floor3_slab',
    center: { x: -1.5, y: 7.2, z: 0 },
    size: { x: 9.0, y: 0.1, z: 12.0 },
    isWalkable: true
  }));
  colliders.push(new BoxCollider({
    name: 'hero_floor4_rooftop_slab',
    center: { x: 0, y: 10.8, z: 0 },
    size: { x: 12.0, y: 0.1, z: 12.0 },
    isWalkable: true
  }));

  // 3. Stair Ramp Colliders (Continuous slope for all 3 flights)
  // Flight 1: (Floor 1 -> Floor 2)
  colliders.push(new StairRampCollider({
    name: 'hero_flight_1_stairs',
    bottom: { x: -2.25, y: 0.0, z: -3.5 },
    top: { x: -2.25, y: 3.6, z: 1.5 },
    width: 2.5
  }));
  // Flight 2: (Floor 2 -> Floor 3)
  colliders.push(new StairRampCollider({
    name: 'hero_flight_2_stairs',
    bottom: { x: 2.25, y: 3.6, z: 3.5 },
    top: { x: 2.25, y: 7.2, z: -1.5 },
    width: 2.5
  }));
  // Flight 3: (Floor 3 -> Floor 4)
  colliders.push(new StairRampCollider({
    name: 'hero_flight_3_stairs',
    bottom: { x: -2.25, y: 7.2, z: -3.5 },
    top: { x: -2.25, y: 10.8, z: 1.5 },
    width: 2.5
  }));

  // 4. Rooftop Guardrails (Prevent falling off roof)
  colliders.push(
    new BoxCollider({ name: 'hero_f4_rail_north', center: { x: 0, y: 11.4, z: -6.0 }, size: { x: 12.2, y: 1.2, z: 0.4 }, isWalkable: false }),
    new BoxCollider({ name: 'hero_f4_rail_south', center: { x: 0, y: 11.4, z: 6.0 }, size: { x: 12.2, y: 1.2, z: 0.4 }, isWalkable: false }),
    new BoxCollider({ name: 'hero_f4_rail_west', center: { x: -6.0, y: 11.4, z: 0 }, size: { x: 0.4, y: 1.2, z: 12.2 }, isWalkable: false }),
    new BoxCollider({ name: 'hero_f4_rail_east', center: { x: 6.0, y: 11.4, z: 0 }, size: { x: 0.4, y: 1.2, z: 12.2 }, isWalkable: false })
  );

  return colliders;
}
